import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { signIn, signOut, send2FACode, verify2FACode, checkAdmin } from "../lib/auth";
import { SEO } from "../components/SEO";

type Step = "password" | "code";

export function LoginPage() {
  const { session, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const from = (location.state as { from?: string })?.from || "/admin/help";

  useEffect(() => {
    if (!loading && session && isAdmin) {
      navigate(from, { replace: true });
    }
  }, [session, isAdmin, loading, navigate, from]);

  useEffect(() => {
    if (step === "code" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);

    const { data, error } = await signIn(email, password);
    if (error) {
      setError(error.message);
      setSubmitting(false);
      return;
    }

    const userId = data.user?.id;
    if (!userId) {
      setError("Unable to verify credentials. Please try again.");
      setSubmitting(false);
      return;
    }

    const admin = await checkAdmin(userId);
    if (!admin) {
      await signOut();
      setError("You do not have administrator privileges for the Help Centre.");
      setSubmitting(false);
      return;
    }

    await signOut();

    const { error: otpError } = await send2FACode(email);
    if (otpError) {
      setError("Failed to send verification code. Please try again.");
      setSubmitting(false);
      return;
    }

    setStep("code");
    setInfo(`A 6-digit verification code has been sent to ${email}. Enter it below to complete sign-in.`);
    setResendCooldown(60);
    setSubmitting(false);
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { data, error } = await verify2FACode(email, code.trim());
    if (error) {
      setError("Invalid or expired code. Please try again.");
      setSubmitting(false);
      return;
    }

    const userId = data.user?.id;
    if (!userId) {
      setError("Verification failed. Please try again.");
      setSubmitting(false);
      return;
    }

    const admin = await checkAdmin(userId);
    if (!admin) {
      await signOut();
      setError("You do not have administrator privileges for the Help Centre.");
      setStep("password");
      setCode("");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setError(null);
    setInfo(null);
    setSubmitting(true);

    const { error: otpError } = await send2FACode(email);
    if (otpError) {
      setError("Failed to resend verification code. Please try again.");
      setSubmitting(false);
      return;
    }

    setInfo(`A new verification code has been sent to ${email}.`);
    setResendCooldown(60);
    setSubmitting(false);
  }

  function handleBack() {
    setStep("password");
    setCode("");
    setError(null);
    setInfo(null);
  }

  return (
    <>
      <SEO
        title="Admin Login — TrustReq Help Centre"
        description="Administrator login for the TrustReq Help Centre."
        canonical="https://help.trustreq.ca/admin/help/login"
      />
      <div className="login-page">
        <div className="login-card">
          <h1 className="login-title">Help Centre Admin</h1>
          <p className="login-subtitle">
            {step === "password" ? "Sign in to manage help articles" : "Enter your verification code"}
          </p>

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="login-form">
              <div className="form-field">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <div className="form-field">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              {error && <div className="form-error">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? "Verifying..." : "Continue"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={handleCodeSubmit} className="login-form">
              {info && <div className="form-info">{info}</div>}
              <div className="form-field">
                <label htmlFor="code" className="form-label">Verification code</label>
                <input
                  id="code"
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="form-input login-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  autoComplete="one-time-code"
                />
              </div>
              {error && <div className="form-error">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || code.length !== 6}
              >
                {submitting ? "Verifying..." : "Verify and sign in"}
              </button>
              <div className="login-code-actions">
                <button
                  type="button"
                  className="login-link-btn"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || submitting}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
                <button
                  type="button"
                  className="login-link-btn"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
